<template>
  <div>
    <PageHeader
      title="Actuarial benchmarks"
      subtitle="Benchmarks your actuaries deliver by TIN and CCN, and the record in Kairon each one links to."
      :heading-level="1"
    />
    <div class="px-6 py-4 space-y-4">
      <LoadingIndicator v-if="loading" message="Loading benchmarks..." />
      <template v-else-if="data">
        <section class="bg-white rounded-lg border border-gray-200">
          <div class="px-4 py-3 border-b border-gray-100 text-xs font-semibold text-gray-500">Deliveries</div>
          <table class="w-full text-[13px]">
            <thead>
              <tr class="text-xs text-gray-500 border-b border-gray-100">
                <th class="text-left font-medium px-4 py-2">File</th>
                <th class="text-left font-medium px-4 py-2">Source</th>
                <th class="text-left font-medium px-4 py-2">Delivered</th>
                <th class="text-left font-medium px-4 py-2">Loaded</th>
                <th class="text-right font-medium px-4 py-2">Rows</th>
                <th class="text-right font-medium px-4 py-2">TINs</th>
                <th class="text-right font-medium px-4 py-2">CCNs</th>
                <th class="text-left font-medium px-4 py-2">Years</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="d in data.deliveries" :key="d.FILE_NAME" class="border-b border-gray-100 last:border-b-0">
                <td class="px-4 py-2 font-medium text-gray-900">{{ d.FILE_NAME }}</td>
                <td class="px-4 py-2 text-gray-700">{{ d.SOURCE }}</td>
                <td class="px-4 py-2 text-gray-700">{{ fmtDate(d.DELIVERED_AT) }}</td>
                <td class="px-4 py-2 text-gray-700">{{ fmtDate(d.LOADED_AT) }}</td>
                <td class="px-4 py-2 text-right tabular-nums">{{ d.ROWS_LOADED.toLocaleString() }}</td>
                <td class="px-4 py-2 text-right tabular-nums">{{ d.TINS }}</td>
                <td class="px-4 py-2 text-right tabular-nums">{{ d.CCNS }}</td>
                <td class="px-4 py-2 text-gray-700">{{ d.FIRST_YEAR }}–{{ d.LAST_YEAR }}</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section class="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div class="flex flex-wrap items-start gap-4 px-4 py-3 border-b border-gray-100">
            <FilterTabs label="Linked" :model-value="linkFilter" :options="linkOptions" @update:model-value="linkFilter = $event" />
            <FilterTabs label="Type" :model-value="typeFilter" :options="typeOptions" @update:model-value="typeFilter = $event" />
          </div>
          <div class="flex flex-wrap gap-x-6 gap-y-1 px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
            <span><span class="font-semibold text-gray-900">{{ data.coverage.participants }}</span> participants in the file ({{ data.coverage.tins }} TINs, {{ data.coverage.ccns }} CCNs)</span>
            <span><span class="font-semibold text-gray-900">{{ data.coverage.matched }}</span> linked to {{ data.coverage.entities }} records</span>
            <span><span class="font-semibold text-gray-900">{{ data.coverage.unmatched }}</span> with no matching TIN or CCN in Kairon</span>
          </div>
          <table class="w-full text-[13px]">
            <thead>
              <tr class="text-xs text-gray-500 border-b border-gray-200 bg-gray-50">
                <th class="text-left font-semibold px-4 py-2">ID</th>
                <th class="text-left font-semibold px-4 py-2">Name in file</th>
                <th class="text-left font-semibold px-4 py-2">Region</th>
                <th class="text-right font-semibold px-4 py-2">Benchmark</th>
                <th class="text-left font-semibold px-4 py-2">Trend</th>
                <th class="text-right font-semibold px-4 py-2">Regional</th>
                <th class="text-left font-semibold px-4 py-2">Trend</th>
                <th class="text-left font-semibold px-4 py-2">Linked to</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in shown" :key="p.ID_TYPE + p.ID_VALUE" class="border-b border-gray-100 last:border-b-0">
                <td class="px-4 py-2 whitespace-nowrap">
                  <span class="rounded px-1.5 py-0.5 text-xs font-semibold bg-neutral-50 text-neutral-900 mr-1.5">{{ p.ID_TYPE }}</span>
                  <span class="tabular-nums sensitive">{{ p.ID_VALUE }}</span>
                </td>
                <td class="px-4 py-2 text-gray-900 sensitive">{{ p.PARTICIPANT_NAME }}</td>
                <td class="px-4 py-2 text-gray-600">{{ p.REGION }}</td>
                <td class="px-4 py-2 text-right tabular-nums">{{ dollars(p.BENCHMARK_PMPY) }} <span class="text-xs text-gray-400">{{ p.LAST_YEAR }}</span></td>
                <td class="px-4 py-2"><span class="rounded px-1.5 py-0.5 text-xs font-medium" :style="chip(p.BENCHMARK_TREND)">{{ p.BENCHMARK_TREND }}</span></td>
                <td class="px-4 py-2 text-right tabular-nums text-gray-700">{{ dollars(p.REGIONAL_BENCHMARK_PMPY) }}</td>
                <td class="px-4 py-2"><span class="rounded px-1.5 py-0.5 text-xs font-medium" :style="chip(p.REGIONAL_TREND)">{{ p.REGIONAL_TREND }}</span></td>
                <td class="px-4 py-2">
                  <template v-if="p.linked.length">
                    <router-link v-for="e in p.linked" :key="e.id" :to="`/entity/${e.id}`" class="block text-gray-900 hover:underline sensitive">
                      <span class="sensitive">{{ e.name }}</span> <span class="text-xs text-gray-500 sensitive">{{ e.type }}</span>
                    </router-link>
                  </template>
                  <span v-else class="text-gray-400">No record with this {{ p.ID_TYPE }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue'
import FilterTabs from '@/components/widgetcomponents/library/FilterTabs.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'

const TREND = {
  Tailwind: { background: '#E3F3E0', color: '#282C27' },
  Headwind: { background: '#FDECEC', color: '#D53D3D' },
  Neutral: { background: '#F1F1F1', color: '#626264' },
}

export default {
  name: 'BenchmarksTab',
  components: { PageHeader, FilterTabs, LoadingIndicator },
  data() {
    return {
      loading: true,
      data: null,
      linkFilter: 'all',
      linkOptions: [
        { value: 'all', label: 'All' },
        { value: 'linked', label: 'Linked' },
        { value: 'unlinked', label: 'Not linked' },
      ],
      typeFilter: 'all',
      typeOptions: [
        { value: 'all', label: 'All' },
        { value: 'TIN', label: 'TIN' },
        { value: 'CCN', label: 'CCN' },
      ],
    }
  },
  computed: {
    shown() {
      return (this.data.participants || []).filter((p) => {
        if (this.linkFilter === 'linked' && !p.linked.length) return false
        if (this.linkFilter === 'unlinked' && p.linked.length) return false
        if (this.typeFilter !== 'all' && p.ID_TYPE !== this.typeFilter) return false
        return true
      })
    },
  },
  async created() {
    try {
      const { data } = await this.$axios.get('/api/growth/actuarial/feed/')
      this.data = data
    } finally {
      this.loading = false
    }
  },
  methods: {
    chip(trend) {
      return TREND[trend] || TREND.Neutral
    },
    dollars(v) {
      return v == null ? '' : `$${Math.round(v).toLocaleString()}`
    },
    fmtDate(d) {
      if (!d) return ''
      const [y, m, day] = String(d).slice(0, 10).split('-').map(Number)
      return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
  },
}
</script>
